import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Star, X } from "lucide-react";
import { Button } from "../ui/button";
import RatingModal from "../RatingModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISSED_KEY = "rollm8s_pending_rating_dismissed";

/**
 * Dashboard banner that surfaces the most recent completed booking the user
 * hasn't rated yet. Opens the shared <RatingModal /> (same component used on
 * BookingPage) so the rate-trip UI has a single source of truth.
 */
export const PostTripReviewPrompt = () => {
  const [pending, setPending] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]"));
    } catch {
      return new Set();
    }
  });
  const [showModal, setShowModal] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { data } = await axios.get(`${API}/ratings/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const next = (data || []).find((p) => !dismissed.has(p.booking_id));
      setPending(next || null);
    } catch {
      /* soft-fail */
    }
  }, [dismissed]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const persistDismiss = (bookingId) => {
    const next = new Set(dismissed);
    next.add(bookingId);
    setDismissed(next);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...next]));
  };

  const dismiss = () => {
    if (!pending) return;
    persistDismiss(pending.booking_id);
    setPending(null);
  };

  const onRated = () => {
    if (pending) persistDismiss(pending.booking_id);
    setPending(null);
    fetchPending();
  };

  if (!pending) return null;

  const targetLabel = pending.role === "rider" ? "driver" : "rider";

  return (
    <>
      {/* mt clears the fixed mobile navbar (~96px on Samsung with safe-area
          status bar). Lives on the PROMPT itself — not the parent layout —
          so when the rating is dismissed/completed the component returns
          null and the page snaps back to navbar-flush layout. Desktop has
          its own greeting block above this, so only adds modest mt. */}
      {/* The DashboardPage parent already has a mobile spacer div below the
          fixed <header> that clears the navbar (see DashboardPage.js line 643:
          `<div className="md:hidden" style="height: calc(5rem + env(safe-area-inset-top,0px) + 0.25rem)" />`).
          So this card needs NO top margin to clear the navbar — adding mt-24
          here causes a huge empty gap above the card. Just mb-6 to space it
          from the video below it. */}
      <div
        className="relative rounded-2xl p-4 border bg-gradient-to-br from-amber-500/10 to-pink-500/10 border-amber-500/30 mb-6"
        data-testid="post-trip-review-prompt"
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 p-1 rounded-full text-muted-foreground hover:bg-muted/30"
          aria-label="Dismiss"
          data-testid="post-trip-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-muted/30 flex items-center justify-center overflow-hidden shrink-0">
            {pending.other_user_photo ? (
              <img
                src={pending.other_user_photo}
                alt={pending.other_user_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="font-bold">
                {(pending.other_user_name || "?").charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold">How was your {targetLabel}?</p>
            <p className="text-xs text-muted-foreground truncate">
              Rate {pending.other_user_name}
              {pending.event_name ? ` · ${pending.event_name}` : ""}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setShowModal(true)}
            className="shrink-0"
            data-testid="post-trip-rate-btn"
          >
            <Star className="w-3.5 h-3.5 mr-1.5" /> Rate
          </Button>
        </div>
      </div>

      <RatingModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        bookingId={pending.booking_id}
        recipientName={pending.other_user_name}
        recipientType={targetLabel}
        recipientPhoto={pending.other_user_photo}
        onRatingSubmitted={onRated}
      />
    </>
  );
};

export default PostTripReviewPrompt;
